import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import {
  IndicatorByService,
  IndicatorSectionKey,
  indicatorSectionsInOrder,
  IndicatorWithUpdate,
  Service,
  stageIdToString,
} from '../../../../ReportBuilder/Components/ImpactIndicator/utils';
import {
  IndicatorDetails,
  SectionIndicators,
} from '../../../../ReportBuilder/Components/IndicatorDetails/utils';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { mergeClassNames } from 'shared/utils/mergeClassNames';
import { TrueImpactFile } from 'shared/design-system/components/TrueImpactFileUploadBox';
import ImpactStageRender from './ImpactStageRender';
import ImpactAddStage from './ImpactAddStage';
import { FeedbackCommentBodyWithIndex } from '../../../utils';
import {
  ErrorsByIndicatorId,
  HandleUpdatePropertyData,
  ModelSaveWarning,
} from './modelErrors';
import { useIndicator } from 'shared/providers/IndicatorProvider';

export const isAnyIndicatorHasFinalResults = (
  indicatorDetails: SectionIndicators,
): boolean => {
  for (const s of indicatorSectionsInOrder) {
    for (const i of indicatorDetails[s]) {
      if (i.finalForecast) return true;
    }
  }
  return false;
};

const getIndicatorById = (
  sectionKey: IndicatorSectionKey,
  indicatorId: number,
  impactDetails: SectionIndicators,
) => {
  const i = impactDetails[sectionKey].findIndex(
    x => x.indicatorId === indicatorId,
  );
  if (i === -1) return;
  return {
    index: i,
    indicator: impactDetails[sectionKey][i],
  };
};

export type ImpactIndicatorById = {
  [id: number]: {
    index: number;
    indicator: IndicatorDetails;
    sectionKey: IndicatorSectionKey;
    impactSummaryIndex: number;
    impactSummaryIndicator: IndicatorWithUpdate | undefined;
  };
};

export type HandleDeleteIndicatorData = {
  sectionKey: IndicatorSectionKey;
  indicatorId: number;
  impactSummaryIndex: number;
  impactDetailIndex: number;
  impactSummaryIndicator: IndicatorWithUpdate;
  impactDetailIndicator: IndicatorDetails;
};
export type HandleAddIndicatorData = {
  updatedText: string;
  measurement: string;
  forecast?: number | string;
  finalForecast?: number | string;
  file: TrueImpactFile | undefined;
};

interface ImpactTableStageProps {
  reportId: number;
  service: Service;
  programName: string;
  isReportPassed: boolean;
  sectionKey: IndicatorSectionKey;
  impactDetails: SectionIndicators;
  impactSummary: IndicatorByService;
  allIndicators: IndicatorByService;
  errorsByIndicator: ErrorsByIndicatorId;
  feedback: FeedbackCommentBodyWithIndex[];
  impactIndicatorsById: ImpactIndicatorById;
  warningsByIndicator: ErrorsByIndicatorId;
  handleDeleteSection: (sectionKey: IndicatorSectionKey) => unknown;
  handleDeleteIndicator: (v: HandleDeleteIndicatorData) => unknown;
  handleAddIndicator: (
    sectionKey: IndicatorSectionKey,
    indicator: IndicatorWithUpdate,
    data: HandleAddIndicatorData,
  ) => unknown;
  handleUpdateProperty: (data: HandleUpdatePropertyData) => unknown;
  className?: string;
  onEditChange: (warning: ModelSaveWarning, isEditing: boolean) => void;
}

const StyledDiv = styled.div`
  border-bottom: 1px solid ${COLORS.grayLight};
  &:last-child {
    border-bottom: none;
  }
  .error-text-field {
    color: ${COLORS.critical};
  }
  .warning-text-field {
    color: ${COLORS.warningDark};
  }
  .results-column {
    padding-left: ${SPACERS.small};
    padding-right: ${SPACERS.small};
    background-color: ${COLORS.grayLightest};
    border-left: 1px solid ${COLORS.grayLight};
  }
  .stage-column,
  .metric-column,
  .results-column {
    padding-top: ${SPACERS.large};
    padding-bottom: ${SPACERS.large};
  }
  .stage-column {
    width: 100%;
    background-color: ${COLORS.grayLightest};
    border-right: 1px solid ${COLORS.grayLight};
    .delete-stage-button {
      margin-top: ${SPACERS.tiny};
    }
  }
  .metric-column {
    padding-left: ${SPACERS.large};
    padding-right: ${SPACERS.extraLarge};
    .editable-text {
      display: flex;
      gap: ${SPACERS.medium};
      justify-content: space-between;
    }
    .editable-text.editable-text-margin {
      margin-bottom: ${SPACERS.small};
    }
    .definition-text {
      margin-bottom: ${SPACERS.small};
    }
    .delete-button {
      margin-top: ${SPACERS.small};
    }
    .add-indicator-modal {
      justify-content: center;
    }
    .report-file {
      margin-top: ${SPACERS.tiny};
    }
  }
  .metric-column.metric-column-border {
    border-bottom: 1px solid ${COLORS.grayLight};
  }
  .forecast-text {
    margin-bottom: ${SPACERS.tiny};
  }
  .final-forecast-text {
    margin-top: ${SPACERS.tiny};
  }
  .section-comments-button {
    margin-top: 0;
    justify-content: center;
  }
`;

const ImpactTableStage: React.FC<ImpactTableStageProps> = ({
  reportId,
  feedback,
  sectionKey,
  impactDetails,
  impactSummary,
  allIndicators,
  isReportPassed,
  errorsByIndicator,
  warningsByIndicator,
  impactIndicatorsById,
  handleAddIndicator,
  handleDeleteIndicator,
  handleDeleteSection,
  handleUpdateProperty,
  className,
  onEditChange,
}) => {
  const { getMissingIndicatorDetails } = useIndicator();
  const [open, setOpen] = useState<boolean>(false);

  const handleOpen = useCallback(() => setOpen(true), [setOpen]);
  const handleClose = useCallback(() => setOpen(false), [setOpen]);

  const handleAdd = useCallback(
    (indicator: IndicatorWithUpdate) => {
      handleAddIndicator(sectionKey, indicator, {
        updatedText: '',
        measurement: '',
        forecast: undefined,
        finalForecast: undefined,
        file: undefined,
      });
      setOpen(false);
    },
    [sectionKey, handleAddIndicator, setOpen],
  );

  const isSucceed = sectionKey === 'succeed';
  const stageName = stageIdToString(sectionKey);
  const lastIndex = impactSummary[sectionKey].length - 1;
  const isMoreThanOne = impactSummary[sectionKey].length > 1;

  return (
    <StyledDiv
      className={mergeClassNames(
        className,
        isSucceed && 'bottom-left-radius',
        isSucceed && 'bottom-right-radius',
      )}
    >
      {(impactSummary[sectionKey].length === 0 ||
        impactDetails[sectionKey].length === 0) && (
        <ImpactAddStage
          stageName={stageName}
          sectionKey={sectionKey}
          allIndicators={allIndicators}
          handleAddIndicator={handleAddIndicator}
        />
      )}
      {impactSummary[sectionKey].map((i, impactSummaryIndex) => {
        const indicatorById =
          impactIndicatorsById[i.id] ||
          getIndicatorById(sectionKey, i.id, impactDetails);
        if (!indicatorById) {
          getMissingIndicatorDetails(i.id, sectionKey, i, impactSummaryIndex);
          return null;
        }
        const indicator: HandleDeleteIndicatorData = {
          impactSummaryIndicator: i,
          indicatorId: i.id,
          impactDetailIndicator: indicatorById.indicator,
          impactDetailIndex: indicatorById.index,
          impactSummaryIndex: impactSummaryIndex,
          sectionKey: sectionKey,
        };
        return (
          <ImpactStageRender
            key={i.id}
            isSucceed={isSucceed}
            errorsByIndicator={errorsByIndicator}
            impactSummaryIndex={impactSummaryIndex}
            lastIndex={lastIndex}
            handleDeleteSection={handleDeleteSection}
            sectionKey={sectionKey}
            stageName={stageName}
            isMoreThanOne={isMoreThanOne}
            reportId={reportId}
            indicator={indicator}
            allIndicators={allIndicators}
            warningsByIndicator={warningsByIndicator}
            handleUpdateProperty={handleUpdateProperty}
            indicatorById={indicatorById}
            handleDeleteIndicator={handleDeleteIndicator}
            handleAdd={handleAdd}
            open={open}
            handleClose={handleClose}
            handleOpen={handleOpen}
            isReportPassed={isReportPassed}
            impactSummary={impactSummary}
            impactDetails={impactDetails}
            feedback={feedback}
            onEditChange={onEditChange}
          />
        );
      })}
    </StyledDiv>
  );
};

export default React.memo(ImpactTableStage);
